import { FIELD_META_BY_KEY, INITIALS_META, PRESET_META } from './constants'
import type { Box, Preset, Tool } from './types'

export function getBoxTool(b: Box): Tool {
	if (b.type === 'SIGNATURE' || b.type === 'INITIALS') return b.type
	return (b.preset ?? 'CUSTOM') as Tool
}

export function isInitialsLabel(label: unknown): boolean {
	return (
		typeof label === 'string' &&
		label.trim().toLowerCase() === INITIALS_META.label.toLowerCase()
	)
}

export function isInitialsFieldMeta(
	fieldMeta: Record<string, unknown> | null | undefined,
): boolean {
	if (!fieldMeta || typeof fieldMeta !== 'object') return false

	const preset = String(
		(fieldMeta as { preset?: string }).preset ?? '',
	).toUpperCase()
	if (preset === 'INITIALS') return true

	const metaType = String(
		(fieldMeta as { type?: string }).type ?? '',
	).toLowerCase()
	if (metaType === 'initials') return true

	return isInitialsLabel((fieldMeta as { label?: string }).label)
}

export function resolveESignFieldType(field: {
	type?: string | null
	label?: string | null
	meta?: Record<string, unknown> | null
}): string {
	const upperType = String(field.type ?? 'TEXT').toUpperCase()
	if (upperType === 'INITIALS' || upperType === 'SIGNATURE') {
		return upperType
	}
	if (
		upperType === 'TEXT' &&
		(isInitialsLabel(field.label) || isInitialsFieldMeta(field.meta))
	) {
		return 'INITIALS'
	}
	return upperType
}

export function genId(): string {
	return typeof crypto !== 'undefined' && 'randomUUID' in crypto
		? crypto.randomUUID()
		: Math.random().toString(36).slice(2)
}

export function metaForPreset(p: Preset | undefined) {
	const v = (p && FIELD_META_BY_KEY[p]) ?? FIELD_META_BY_KEY.CUSTOM
	return {
		type: p === 'INITIALS' ? ('initials' as const) : ('text' as const),
		label: v.label,
		placeholder: v.placeholder,
		required: v.required,
		characterLimit: v.characterLimit,
	}
}

export function presetLabel(p: Preset | undefined): string {
	return ((p && PRESET_META[p as Preset]) ?? PRESET_META.CUSTOM).label
}

export function presetFromFieldMeta(
	fieldMeta: Record<string, unknown> | undefined,
): Preset | undefined {
	if (!fieldMeta || typeof fieldMeta !== 'object') return undefined
	const preset = (fieldMeta as { preset?: Preset }).preset
	if (preset && preset in PRESET_META) return preset as Preset
	const label = String((fieldMeta as { label?: string }).label ?? '').trim()
	const byLabel = (
		Object.entries(PRESET_META) as [Preset, { label: string }][]
	).find(([, meta]) => meta.label === label)
	return byLabel ? byLabel[0] : undefined
}

export function isTextTool(t: Tool): boolean {
	return t !== 'SIGNATURE' && t !== 'INITIALS'
}
